import { z } from "zod";
import { AuditTrailEntryType } from "../AuditTrailEntryType.js";
// Form / form-context / form-draft / SOAD-amendment audit `data` schemas. See
// `registry.ts` for the audit-data leniency contract these follow.
// The recurring `{ id, version }` form-template reference.
export const formIdAndVersionSchema = z.object({
    id: z.string(),
    version: z.string(),
});
export const createFormDataSchema = z.object({
    formTemplate: formIdAndVersionSchema,
    formContext: z.unknown().optional(),
    patientId: z.string().nullish(),
    workItem: z.unknown().optional(),
    linkedForms: z.unknown().optional(),
    formPart: z.number().nullish(),
    placeOfSafety: z.unknown().optional(),
});
export const uploadFormDataSchema = z.object({
    formTemplate: formIdAndVersionSchema,
    supportingFormsDetails: z.unknown().optional(),
});
export const signFormDataSchema = z.object({
    formTemplate: formIdAndVersionSchema,
    formVersion: z.number(),
    isTrainingForm: z.boolean(),
    action: z.string().nullish(),
    part: z.number().nullish(),
    formId: z.string().nullish(),
    formDraftId: z.string().nullish(),
    userId: z.string().nullish(),
    userName: z.string().nullish(),
    teamId: z.string().nullish(),
    teamName: z.string().nullish(),
    organisationId: z.string().nullish(),
    organisationName: z.string().nullish(),
    supportingFormsDetails: z.unknown().optional(),
});
export const claimOutcomeFormDataSchema = z.object({
    formId: z.string(),
    formTemplate: formIdAndVersionSchema,
    previousOwnerUserId: z.string().nullable(),
    newOwnerUserId: z.string(),
    posDisplayName: z.string(),
});
export const getFormPdfDataSchema = z.object({
    includeAllLinkedForms: z.boolean().nullish(),
    includeLinkedForms: z.array(z.string()).nullish(),
    formTemplate: formIdAndVersionSchema.nullish(),
    isTrainingForm: z.boolean().nullish(),
    formVersion: z.number().nullish(),
    formContextType: z.string().nullish(),
});
// deleteForm and deleteUploadedForm share a shape.
export const deleteFormDataSchema = z.object({
    formTemplate: formIdAndVersionSchema,
    isTrainingForm: z.boolean().nullish(),
    formVersion: z.number().nullish(),
});
export const expireFormDataSchema = z.object({
    expiryDateTime: z.string(),
    formTemplate: formIdAndVersionSchema,
    formPart: z.number(),
    formId: z.string(),
});
export const createFormContextDataSchema = z.object({
    // formContextType and type are alternative keys for the same value.
    formContextType: z.string().nullish(),
    type: z.string().nullish(),
});
export const updateFormContextOfficialReferenceIdDataSchema = z.object({
    from: z.string().nullish(),
    to: z.string(),
});
export const getFormDraftPdfDataSchema = z.object({
    formContextType: z.string().nullish(),
    formTemplate: formIdAndVersionSchema.nullish(),
    formVersion: z.number().nullish(),
    isTrainingForm: z.boolean().nullish(),
});
// Cancel/delete share this shape; start adds reason fields.
export const soadAmendmentDataSchema = z.object({
    formTemplate: formIdAndVersionSchema,
    formVersion: z.number(),
    isTrainingForm: z.boolean(),
});
export const startSoadAssessmentAmendmentDataSchema = soadAmendmentDataSchema.extend({
    reason: z.string(),
    reasonDescription: z.string().nullish(),
});
// The SOAD's restricted-disclosure judgement, as certified on one version of a
// certificate. The reasons text itself is deliberately not carried here: it
// is already the form's own versioned data, and copying clinical free text into
// the audit trail widens exposure without adding accountability.
export const recordSoadDisclosureHarmDataSchema = z.object({
    formTemplate: formIdAndVersionSchema,
    formVersion: z.number(),
    isTrainingForm: z.boolean(),
    disclosureHarmful: z.boolean().nullish(),
    // The judgement on the version this one supersedes, so a signature that
    // withdrew a restriction is legible from the entry alone. Absent on a first
    // version, and on entries written before it was carried.
    previousDisclosureHarmful: z.boolean().nullish(),
});
// Shared empty payload.
export const emptyFormDataSchema = z.object({});
// Audit type → schema entries for this file, composed in ../registry.ts.
export const formAuditDataRegistry = {
    [AuditTrailEntryType.createForm]: createFormDataSchema,
    [AuditTrailEntryType.uploadForm]: uploadFormDataSchema,
    [AuditTrailEntryType.signForm]: signFormDataSchema,
    [AuditTrailEntryType.claimOutcomeForm]: claimOutcomeFormDataSchema,
    [AuditTrailEntryType.getFormPdf]: getFormPdfDataSchema,
    [AuditTrailEntryType.deleteForm]: deleteFormDataSchema,
    [AuditTrailEntryType.deleteUploadedForm]: deleteFormDataSchema,
    [AuditTrailEntryType.expireForm]: expireFormDataSchema,
    [AuditTrailEntryType.createFormPushEvent]: emptyFormDataSchema,
    [AuditTrailEntryType.createFormContext]: createFormContextDataSchema,
    [AuditTrailEntryType.updateFormContextOfficialReferenceId]: updateFormContextOfficialReferenceIdDataSchema,
    [AuditTrailEntryType.bulkFormDownload]: emptyFormDataSchema,
    [AuditTrailEntryType.createFormDraft]: emptyFormDataSchema,
    [AuditTrailEntryType.getFormDraft]: emptyFormDataSchema,
    [AuditTrailEntryType.deleteFormDraft]: emptyFormDataSchema,
    [AuditTrailEntryType.getFormDraftPdf]: getFormDraftPdfDataSchema,
    [AuditTrailEntryType.startSoadAssessmentAmendment]: startSoadAssessmentAmendmentDataSchema,
    [AuditTrailEntryType.cancelSoadAssessmentAmendment]: soadAmendmentDataSchema,
    [AuditTrailEntryType.deleteSoadAssessment]: soadAmendmentDataSchema,
    [AuditTrailEntryType.recordSoadDisclosureHarm]: recordSoadDisclosureHarmDataSchema,
    [AuditTrailEntryType.updateFormContextAssessmentLocation]: emptyFormDataSchema,
};
//# sourceMappingURL=form.js.map